import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { cn, useEscapeKey, useOnClickOutside } from '@/lib/utils';
import './Menu.css';

/** O que o gatilho precisa espalhar no próprio `<button>` (ou num `IconButton`). */
export interface MenuTriggerProps {
  ref: Ref<HTMLButtonElement>;
  'aria-expanded': boolean;
  'aria-controls': string;
  onClick: () => void;
}

export interface MenuProps {
  /** Desenha o gatilho com as props do disclosure (estado, painel controlado e clique). */
  trigger: (props: MenuTriggerProps) => ReactNode;
  /** O conteúdo do painel: uma `MenuList` ou uma seção própria (ex.: os avisos do sino). */
  children: ReactNode;
  /** Borda do gatilho a que o painel se alinha. */
  align?: 'start' | 'end';
  /** Avisado a cada abertura e fechamento (ex.: o sino marca os avisos como vistos). */
  onOpenChange?: (open: boolean) => void;
  className?: string;
  panelClassName?: string;
}

const MenuContext = createContext<(() => void) | null>(null);

/**
 * Painel ancorado a um botão no padrão **disclosure** (não `role="menu"`): o
 * gatilho leva `aria-expanded`/`aria-controls`, o painel é conteúdo comum e o
 * Tab percorre os itens na ordem do documento. Esc, clique fora, troca de rota e
 * clique num item fecham e devolvem o foco ao gatilho. Sem trava de foco nem
 * inércia do resto da página: não é modal.
 */
export function Menu({
  trigger,
  children,
  align = 'end',
  onOpenChange,
  className,
  panelClassName,
}: MenuProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // O hook de clique fora recebe o array nas dependências: ele precisa ser estável.
  const insideRefs = useMemo(() => [rootRef], []);
  // Espelhos em ref: os fechamentos vêm de listeners do documento, fora do render.
  const openRef = useRef(open);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const setOpenState = useCallback((next: boolean): void => {
    if (openRef.current === next) return;
    openRef.current = next;
    setOpen(next);
    onOpenChangeRef.current?.(next);
  }, []);

  const closeAndReturnFocus = useCallback((): void => {
    setOpenState(false);
    triggerRef.current?.focus();
  }, [setOpenState]);

  /**
   * Clique fora: quem clicou noutro controle fica com o foco dele; clicou no
   * vazio, o foco (que iria para o body) volta ao gatilho — depois do clique,
   * quando o navegador já decidiu para onde ele foi.
   */
  const closeFromOutside = useCallback((): void => {
    setOpenState(false);
    window.setTimeout(() => {
      const active = document.activeElement;
      if (active === null || active === document.body) triggerRef.current?.focus();
    }, 0);
  }, [setOpenState]);

  useEscapeKey(closeAndReturnFocus, open);
  useOnClickOutside(insideRefs, closeFromOutside, open);

  // A troca de rota fecha: o painel aberto não sobrevive à navegação.
  const { key: locationKey } = useLocation();
  const lastLocationKey = useRef(locationKey);
  useEffect(() => {
    if (lastLocationKey.current === locationKey) return;
    lastLocationKey.current = locationKey;
    if (open) closeAndReturnFocus();
  }, [locationKey, open, closeAndReturnFocus]);

  /**
   * O Tab que sai do componente fecha sem puxar o foco de volta: a pessoa já
   * foi adiante. Um clique em área não focável do painel não tem destino
   * (`relatedTarget` nulo) e não fecha.
   */
  const onBlur = (event: FocusEvent<HTMLDivElement>): void => {
    const next = event.relatedTarget;
    if (next instanceof Node && !rootRef.current?.contains(next)) setOpenState(false);
  };

  return (
    <div ref={rootRef} className={cn('menu', className)} onBlur={onBlur}>
      {trigger({
        ref: triggerRef,
        'aria-expanded': open,
        'aria-controls': panelId,
        onClick: () => setOpenState(!openRef.current),
      })}
      <div
        id={panelId}
        className={cn('menu__panel', `menu__panel--${align}`, panelClassName)}
        hidden={!open}
      >
        <MenuContext.Provider value={closeAndReturnFocus}>{children}</MenuContext.Provider>
      </div>
    </div>
  );
}

/** A lista de itens do painel (`<ul role="list">`, nunca `role="menu"`). */
export function MenuList({ children }: { children: ReactNode }): JSX.Element {
  return (
    <ul className="menu__list" role="list">
      {children}
    </ul>
  );
}

export interface MenuItemProps {
  label: string;
  icon?: LucideIcon;
  /** Rota interna: o item vira um `<Link>`. */
  to?: string;
  /** Ação: o item vira um `<button>`. */
  onSelect?: () => void;
}

/** Um item do painel: link ou botão; ativá-lo fecha o painel e devolve o foco. */
export function MenuItem({ label, icon: Icon, to, onSelect }: MenuItemProps): JSX.Element {
  const close = useContext(MenuContext);
  const select = (): void => {
    close?.();
    onSelect?.();
  };
  const body = (
    <>
      {Icon ? <Icon className="menu__icon" size={16} aria-hidden="true" /> : null}
      <span>{label}</span>
    </>
  );

  return (
    <li className="menu__entry">
      {to ? (
        <Link to={to} className="menu__item" onClick={select}>
          {body}
        </Link>
      ) : (
        <button type="button" className="menu__item" onClick={select}>
          {body}
        </button>
      )}
    </li>
  );
}

/** Divisor visual entre grupos de itens (sem papel: a lista já agrupa). */
export function MenuDivider(): JSX.Element {
  return <li className="menu__divider" aria-hidden="true" />;
}
